import { all, debounce, put, select, takeLatest } from 'redux-saga/effects'

import { NotificationManager } from '@/components/Notification'
import { cartService, orderService } from '@/services'
import { selectHasSessionBasedShoppingCart } from '@/store/selectors/userSelector'
import { trackCart, updateTrackStatus } from '@/store/slices/adobeAnalyticSlice'
import { getSessionCookieKey } from '@/contexts/punchout/sessionCookieKeyStore'
import { SESSION_BASED_EXPIRY_TIME } from '@/libs/constants'
import { TRACK_DATA } from '@/libs/constants/adobeAnalytics'
import { logErrors } from '@/libs/utils/common'
import { getCookie, renewCookie } from '@/libs/utils/cookies'
import { getErrorData, hasErrorCode } from '@/libs/utils/errorUtils'
import { linkGenerator } from '@/libs/utils/language'

import {
  addProductToCart,
  addProductToCartError,
  changeCartProductReference,
  emptyCart,
  emptyCartFailed,
  emptyCartSucceeded,
  getCartItems,
  getCartItemsAfterRedeemingVoucher,
  getCartItemsAfterRedeemingVoucherError,
  getCartItemsAfterRedeemingVoucherSuccess,
  getCartItemsError,
  getCartItemsSuccess,
  getMiniCart,
  getMiniCartError,
  getMiniCartSuccess,
  getPriceBoxData,
  getPriceBoxDataError,
  getPriceBoxDataSuccess,
  increaseCartItemQuantity,
  increaseCartItemQuantityError,
  increaseCartItemQuantitySuccess,
  removeCartItem,
  removeProductAddedFromQuickEntry,
  removeVoucherFromCart,
  removeVoucherFromCartError,
  removeVoucherFromCartSuccess,
  reorder,
  reorderFailed,
  reorderSucceeded,
  replaceCartItems,
  replaceCartItemsError,
  setCartItemQuantity,
  setCartItemQuantityError,
  setFetchCartStateToIdle,
  updateProductInCart,
} from '../slices/cartSlice'
import mapCartData from './apiDataMapping/cartMapping'

const TIMEOUT_TO_REFETCH_CART = 280

export function* renewShoppingCartSession() {
  const hasSessionBasedShoppingCart = yield select(
    selectHasSessionBasedShoppingCart,
  )

  if (hasSessionBasedShoppingCart) {
    renewCookie(getSessionCookieKey(), SESSION_BASED_EXPIRY_TIME)
  }
}

export function* modifyPayloadIfHavingSessionBasedShoppingCart(payload = {}) {
  const hasSessionBasedShoppingCart = yield select(
    selectHasSessionBasedShoppingCart,
  )

  if (hasSessionBasedShoppingCart) {
    const sessionId = getCookie(getSessionCookieKey())

    if (!sessionId) {
      yield renewShoppingCartSession()
    }

    return {
      ...payload,
      sessionId: getCookie(getSessionCookieKey()),
    }
  }

  return payload
}

function* getCartItemsSaga() {
  try {
    const payload = yield modifyPayloadIfHavingSessionBasedShoppingCart()
    const { data } = yield orderService.getShoppingCart(payload)
    const mappedCartData = mapCartData(data)

    yield put(getCartItemsSuccess(mappedCartData))
    yield put(trackCart({ [TRACK_DATA.CART]: mappedCartData.items }))
    yield renewShoppingCartSession()
  } catch (error) {
    console.error(error)
    yield put(updateTrackStatus({ trackSection: TRACK_DATA.CART }))
    yield put(getCartItemsError())
    NotificationManager.error({
      message: 'notification.error.load',
    })
  }
}

function* getMiniCartDataSaga() {
  try {
    const payload = yield modifyPayloadIfHavingSessionBasedShoppingCart()
    const {
      data: { items },
    } = yield cartService.getMiniCart(payload)

    yield put(getMiniCartSuccess(items))
    yield put(trackCart({ [TRACK_DATA.MINICART]: items }))
  } catch (error) {
    console.error('error when fetching mini cart data', error?.message)
    yield put(updateTrackStatus({ trackSection: TRACK_DATA.MINICART }))
    yield put(getMiniCartError())
    NotificationManager.error({
      message: 'notification.error.miniCart',
    })
  }
}

function* updateProductInCartSaga({ payload }) {
  try {
    const modifiedPayload = yield modifyPayloadIfHavingSessionBasedShoppingCart(payload)

    yield cartService.updateProductInCart(modifiedPayload)
    yield put(
      updateTrackStatus({
        trackSection: TRACK_DATA.CART,
        trackStatus: false,
      }),
    )
    yield put(getCartItems())
    yield renewShoppingCartSession()
  } catch (error) {
    console.error('error when updating product in cart', error?.message)
    yield put(setCartItemQuantityError())
    NotificationManager.error({
      message: 'notification.error.changeCartItemQuantity',
    })
  }
}

function* setCartItemQuantitySaga({ payload }) {
  try {
    const modifiedPayload = yield modifyPayloadIfHavingSessionBasedShoppingCart(payload)

    yield cartService.updateProductInCart(modifiedPayload)
    yield put(
      updateTrackStatus({
        trackSection: TRACK_DATA.CART,
        trackStatus: false,
      }),
    )
    yield put(getCartItems())
    yield renewShoppingCartSession()
  } catch (error) {
    console.error('error when setting cart item quantity', error?.message)
    yield put(setCartItemQuantityError())
    NotificationManager.error({
      message: 'notification.error.changeCartItemQuantity',
    })
  }
}

function* addProductToCartSaga({ payload }) {
  try {
    const modifiedPayload = yield modifyPayloadIfHavingSessionBasedShoppingCart(payload)

    yield cartService.addProductToCart(modifiedPayload)
    yield renewShoppingCartSession()
    yield put(getCartItems())
    NotificationManager.success({
      message: 'notification.success.addCart',
    })
  } catch (error) {
    console.error(error)
    yield put(addProductToCartError())
    if (hasErrorCode(error, '102')) {
      NotificationManager.error({
        message: 'notification.error.addCartMandatorySafetyInstruction',
      })
    } else {
      NotificationManager.error({
        message: 'notification.error.addCart',
      })
    }
  }
}

function* changeCartProductReferenceSaga({ payload }) {
  try {
    yield cartService.updateProductInCart(payload)
    yield put(getCartItems())
  } catch (error) {
    console.error('error when changing cart product reference', error?.message)
    NotificationManager.error({
      message: 'notification.error.changeCartItemProductReference',
    })
  }
}

function* increaseCartItemQuantitySaga({ payload }) {
  try {
    const modifiedPayload = yield modifyPayloadIfHavingSessionBasedShoppingCart(payload)

    const { data } = yield cartService.updateProductInCart(modifiedPayload)

    yield renewShoppingCartSession()
    yield put(increaseCartItemQuantitySuccess(data))
    NotificationManager.success({
      message: 'notification.success.addCart',
    })
  } catch (error) {
    console.error('error when increasing cart item quantity', error?.message)
    yield put(increaseCartItemQuantityError(error))
    NotificationManager.error({
      message: 'notification.error.addCart',
    })
  }
}

function* getPriceBoxDataSaga({ payload }) {
  try {
    const { materialNumber } = payload
    const data = yield orderService.getPriceBoxData({ materialNumber })

    yield put(getPriceBoxDataSuccess(data))
  } catch (error) {
    console.error(error)
    yield put(getPriceBoxDataError())
  }
}

function* removeProductFromCartSaga({ payload }) {
  if (typeof payload === 'string') {
    try {
      const modifiedPayload = yield modifyPayloadIfHavingSessionBasedShoppingCart({
        materialNumber: payload,
      })

      yield cartService.removeProductFromCart(modifiedPayload)
      yield put(getCartItems())
    } catch (error) {
      console.error('error when removing product from cart', error?.message)
      yield put(removeCartItem())
      yield NotificationManager.error({
        message: 'notification.error.delete',
      })
    }
  } else {
    yield put(removeProductAddedFromQuickEntry())
  }

  yield renewShoppingCartSession()
}

function* emptyCartSaga() {
  try {
    const payload = yield modifyPayloadIfHavingSessionBasedShoppingCart()

    yield cartService.emptyCart(payload)
    yield put(emptyCartSucceeded())
    yield renewShoppingCartSession()
    NotificationManager.success({
      message: 'notification.success.submit',
    })
  } catch (error) {
    yield put(emptyCartFailed())
    logErrors(error)
    NotificationManager.error({
      message: 'notification.error.submit',
    })
  }
}

function* removeVoucherFromCartSaga() {
  try {
    const payload = yield modifyPayloadIfHavingSessionBasedShoppingCart()
    yield cartService.removeVoucher(payload)
    yield put(removeVoucherFromCartSuccess())
    yield renewShoppingCartSession()
    yield put(getCartItems())

    NotificationManager.success({
      message: 'notification.success.removeVoucher',
    })
  } catch (error) {
    console.error('error when removing voucher code', error)
    const currentVoucher = yield select(
      (state) => state.cart.cartData.voucherValue?.code,
    )

    yield put(removeVoucherFromCartError(currentVoucher))

    NotificationManager.error({
      message: 'notification.error.removeVoucher',
      description: 'notification.error.somethingWentWrong',
    })
  }
}

function* replaceCartItemsSaga({ payload }) {
  try {
    yield cartService.replaceProductInCart(payload)
    yield renewShoppingCartSession()
    yield put(getCartItems())
  } catch (error) {
    yield put(replaceCartItemsError)
    NotificationManager.error({
      message: 'notification.error.replaceCartProduct',
      description: getErrorData(error),
    })
  }
}

function* getCartItemsAfterRedeemingVoucherSaga() {
  try {
    yield put(
      updateTrackStatus({
        trackSection: TRACK_DATA.CART,
        trackStatus: false,
      }),
    )
    const payload = yield modifyPayloadIfHavingSessionBasedShoppingCart()
    const { data } = yield orderService.getShoppingCart(payload)
    const mappedCartData = mapCartData(data)

    yield put(getCartItemsAfterRedeemingVoucherSuccess(mappedCartData))
    yield put(trackCart({ [TRACK_DATA.CART]: mappedCartData.items }))
    yield renewShoppingCartSession()
  } catch (error) {
    console.error(
      'error when fetching cart items after redeeming voucher',
      error,
    )
    yield put(getCartItemsAfterRedeemingVoucherError())
    NotificationManager.error({
      message: 'notification.error.load',
    })
  }
}

function* requestReorderSaga({ payload }) {
  const { salesOrderNumber, canViewCart, navigate } = payload

  try {
    const modifiedPayload = yield modifyPayloadIfHavingSessionBasedShoppingCart(
      { orderNumber: salesOrderNumber },
    )

    yield cartService.reorder(modifiedPayload)
    yield all([
      put(reorderSucceeded(salesOrderNumber)),
      put(setFetchCartStateToIdle()),
    ])

    NotificationManager.success({
      message: 'notification.success.reOrder',
    })

    if (canViewCart && navigate) {
      navigate(linkGenerator('/cart'))
    }
  } catch (error) {
    console.error('error when reordering', error)
    yield put(reorderFailed(salesOrderNumber))

    NotificationManager.error({
      message: 'notification.error.reOrder',
      description: 'notification.error.somethingWentWrong',
    })

    if (navigate) {
      navigate(linkGenerator('/orders'))
    }
  }
}

export default function* cartSaga() {
  yield takeLatest(getMiniCart.type.toString(), getMiniCartDataSaga)
  yield takeLatest(getCartItems.type.toString(), getCartItemsSaga)
  yield takeLatest(
    getCartItemsAfterRedeemingVoucher.type.toString(),
    getCartItemsAfterRedeemingVoucherSaga,
  )
  yield debounce(
    TIMEOUT_TO_REFETCH_CART,
    increaseCartItemQuantity.type.toString(),
    increaseCartItemQuantitySaga,
  )
  yield takeLatest(getPriceBoxData.type.toString(), getPriceBoxDataSaga)
  yield takeLatest(addProductToCart.type.toString(), addProductToCartSaga)
  yield takeLatest(
    setCartItemQuantity.type.toString(),
    setCartItemQuantitySaga,
  )
  yield takeLatest(removeCartItem.type.toString(), removeProductFromCartSaga)
  yield takeLatest(emptyCart.type.toString(), emptyCartSaga)
  yield takeLatest(
    changeCartProductReference.type.toString(),
    changeCartProductReferenceSaga,
  )
  yield takeLatest(removeVoucherFromCart.type, removeVoucherFromCartSaga)
  yield takeLatest(replaceCartItems.type.toString(), replaceCartItemsSaga)
  yield takeLatest(
    updateProductInCart.type.toString(),
    updateProductInCartSaga,
  )
  yield takeLatest(reorder.type.toString(), requestReorderSaga)
}
